import { stripHtmlTags } from '@change-corgi/core/html';

import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import type { DecisionMakers as ApiDms } from '../../../../hooks/decisionMakers';

import type { DecisionMaker } from './index';

export type AllDmResponses = Array<{
	id: string;
	message: string;
	decisionMaker: { id: string; displayName: string };
}>;

const PRIMARY_ROLE_KEYWORDS = [
	'president',
	'senator',
	'representative',
	'legislature',
	'executive',
	'chair',
	'chairperson',
	'governor',
	'attorney general',
	'commission',
	'commissioner',
	'supervisor',
	'district attorney',
	'sheriff',
	'judge',
	'mayor',
	'council',
	'alderman',
	'chief',
];

export function sortIndividualDms(dms: DecisionMaker[]): DecisionMaker[] {
	return [...dms].sort((a, b) => {
		// First prioritize responses
		const hasResponseA = !!a.response;
		const hasResponseB = !!b.response;
		if (hasResponseA !== hasResponseB) {
			return hasResponseB ? 1 : -1;
		}

		// Next prioritize highest constituentCounts
		const countA = parseInt((a.constituentCount || '0').replace(/,/g, ''), 10) || 0;
		const countB = parseInt((b.constituentCount || '0').replace(/,/g, ''), 10) || 0;
		return countB - countA;
	});
}

export function getConstituencyStat(constituentCount: string | null | undefined): string {
	if (!constituentCount) return '';
	return `Represents ${constituentCount} petition ${constituentCount === '1' ? 'signer' : 'signers'}`;
}

export const getLocation = (state: string | null, title: string | null): string | null => {
	if (!state) return null;

	const usKeywords = ['U.S.', 'US', 'United States', 'State'];
	const hasUsKeyword = usKeywords.some((keyword) => title?.includes(keyword));

	if (hasUsKeyword) return null;

	const fullStateName = getUsState(state);
	if (title?.includes(fullStateName)) return null;

	return fullStateName;
};

export function normalizeDms(dms: ApiDms, allDmResponses: AllDmResponses): DecisionMaker[] {
	return dms.map((dm) => {
		const dmResponse = allDmResponses.find((response) => response.decisionMaker.id === dm.id);
		const constituentCount = dm.targetPetitionAssociationData?.constituentCountAmongSignersLocalized;

		return {
			id: dm.id,
			displayName: dm.displayName,
			slug: dm.slug,
			title: dm.title ? dm.title.replace(/\bUS\b/g, 'U.S.') : null,
			location: getLocation(dm.state, dm.title),
			photoUrl: dm.photo?.userMedium.url ? dm.photo.userMedium.url : null,
			constituentCount,
			signersText: getConstituencyStat(constituentCount),
			publiclyVisible: dm.publiclyVisible,
			...(dmResponse?.message ? { response: stripHtmlTags(dmResponse.message) } : {}),
		};
	});
}

export function getPrimaryDms(politicians: ApiDms): ApiDms {
	const primaries = politicians.filter((dm) => {
		if (!dm.title) return false;
		const title = dm.title.toLowerCase();
		return PRIMARY_ROLE_KEYWORDS.some((role) => title.includes(role));
	});
	return primaries;
}

export function getRawTitle(title: string): string {
	const titleBeforeHyphen = title.split('-')[0].trim();
	return titleBeforeHyphen.replace(/\bUS\b/g, 'U.S.');
}

export function getLegislativeBodies(dms: ApiDms): Array<{ groupTitle: string; groupDms: Array<ApiDms[number]> }> {
	const groups = new Map<string, Array<ApiDms[number]>>();

	for (const dm of dms) {
		if (dm.title) {
			const key = getRawTitle(dm.title);

			// 'Senator' is not a legislative body. Replace with 'Senate'.
			//  Strip 'Former' prefix in attempt to match any existing legislative bodies.
			const groupKey = key.includes('Senator') ? key.replace('Former ', '').replace('Senator', 'Senate') : key;

			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			groups.get(groupKey)!.push(dm);
		}
	}

	return Array.from(groups.entries())
		.filter(([, items]) => items.length > 1)
		.map(([key, items]) => ({ groupTitle: key, groupDms: items }));
}
